import { Controller } from "@hotwired/stimulus"

// Dark, availability-aware date-range picker for the reservation form (and a
// read-only preview in the steward availability editor). Replaces the native
// <input type="date"> pair so we can (a) theme it and (b) colour-code which
// nights are open / booked / closed.
//
// It writes the chosen range into two hidden fields that the reservation-form
// and stripe-reservation controllers already read (check_in_date /
// check_out_date) and fires a `change` so pricing recalculates. Availability
// comes from /properties/:id/calendar.json; the server is authoritative — the
// calendar just prevents obviously-invalid picks up front.
const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]
const MAX_FUTURE_MONTHS = 18 // don't let the user page indefinitely

export default class extends Controller {
  static targets = ["grid", "monthLabel", "summary", "checkInField", "checkOutField", "prev", "next"]
  static values = {
    url: String,
    months: { type: Number, default: 2 },
    readonly: { type: Boolean, default: false },
    checkIn: String,
    checkOut: String,
  }

  connect() {
    this.days = {}              // "YYYY-MM-DD" -> { s, min, max }
    this.fetched = new Set()    // "YYYY-MM" month keys already loaded
    this.anchor = this.checkInValue || null   // selected check-in (ISO) or null
    this.end = this.checkOutValue || null     // selected check-out (ISO) or null
    this.hover = null

    const startISO = this.anchor || this.#iso(new Date())
    this.viewMonth = this.#startOfMonth(this.#parse(startISO))
    const curMonth = this.#startOfMonth(new Date())
    if (this.viewMonth < curMonth) this.viewMonth = curMonth

    this.gridTarget.addEventListener("click", this.#onClick)
    this.gridTarget.addEventListener("mouseover", this.#onHover)
    this.gridTarget.addEventListener("mouseleave", this.#onLeave)

    this.#loadVisible().then(() => this.render())
  }

  disconnect() {
    this.gridTarget.removeEventListener("click", this.#onClick)
    this.gridTarget.removeEventListener("mouseover", this.#onHover)
    this.gridTarget.removeEventListener("mouseleave", this.#onLeave)
  }

  prev() {
    const cur = this.#startOfMonth(new Date())
    if (this.viewMonth <= cur) return
    this.viewMonth = this.#addMonths(this.viewMonth, -1)
    this.#loadVisible().then(() => this.render())
  }

  next() {
    const cap = this.#addMonths(this.#startOfMonth(new Date()), MAX_FUTURE_MONTHS)
    if (this.viewMonth >= cap) return
    this.viewMonth = this.#addMonths(this.viewMonth, 1)
    this.#loadVisible().then(() => this.render())
  }

  // --- data loading -------------------------------------------------------

  #monthsShown() {
    return Array.from({ length: this.monthsValue }, (_, i) => this.#addMonths(this.viewMonth, i))
  }

  async #loadVisible() {
    const first = this.#monthsShown()[0]
    const key = this.#monthKey(first)
    if (this.fetched.has(key) && this.fetched.has(this.#monthKey(this.#monthsShown().at(-1)))) return

    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("start", this.#iso(first))
      url.searchParams.set("months", String(this.monthsValue))
      const res = await fetch(url, { headers: { Accept: "application/json" } })
      if (!res.ok) return
      const data = await res.json()
      if (data.today) this.today = data.today
      Object.assign(this.days, data.days || {})
      this.#monthsShown().forEach((m) => this.fetched.add(this.#monthKey(m)))
    } catch (_e) {
      // Network hiccup — render whatever we have; the server re-validates.
    }
  }

  // --- rendering ----------------------------------------------------------

  render() {
    if (!this.hasGridTarget) return
    this.gridTarget.innerHTML = this.#monthsShown().map((m) => this.#monthHtml(m)).join("")

    const months = this.#monthsShown()
    if (this.hasMonthLabelTarget) {
      const a = months[0]
      const b = months.at(-1)
      this.monthLabelTarget.textContent =
        months.length > 1 && (a.getMonth() !== b.getMonth() || a.getFullYear() !== b.getFullYear())
          ? `${MONTH_NAMES[a.getMonth()].slice(0, 3)} – ${MONTH_NAMES[b.getMonth()].slice(0, 3)} ${b.getFullYear()}`
          : `${MONTH_NAMES[a.getMonth()]} ${a.getFullYear()}`
    }
    if (this.hasPrevTarget) this.prevTarget.disabled = this.viewMonth <= this.#startOfMonth(new Date())
    if (this.hasNextTarget) {
      const cap = this.#addMonths(this.#startOfMonth(new Date()), MAX_FUTURE_MONTHS)
      this.nextTarget.disabled = this.viewMonth >= cap
    }

    this.#applyRangeClasses()
    this.#renderSummary()
  }

  #monthHtml(monthDate) {
    const y = monthDate.getFullYear()
    const mo = monthDate.getMonth()
    const firstWeekday = new Date(y, mo, 1).getDay()
    const daysInMonth = new Date(y, mo + 1, 0).getDate()

    const dow = DOW.map((d) => `<span>${d}</span>`).join("")
    let cells = ""
    for (let i = 0; i < firstWeekday; i++) cells += `<div class="wl-cal-day empty"></div>`
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = this.#iso(new Date(y, mo, d))
      const info = this.days[iso]
      const status = info ? info.s : "closed"
      const cls = `wl-cal-day is-${status}`
      const role = !this.readonlyValue && status === "open" ? ` role="button" tabindex="0"` : ""
      cells += `<div class="${cls}" data-date="${iso}"${role}>${d}</div>`
    }

    return `
      <div class="wl-cal-month">
        <div class="wl-cal-month-name">${MONTH_NAMES[mo]} ${y}</div>
        <div class="wl-cal-dow">${dow}</div>
        <div class="wl-cal-days">${cells}</div>
      </div>`
  }

  // Paints selection/range/preview classes onto the already-rendered cells.
  #applyRangeClasses() {
    const cells = this.gridTarget.querySelectorAll(".wl-cal-day[data-date]")
    const effectiveEnd = this.end || this.#previewEnd()

    cells.forEach((cell) => {
      cell.classList.remove("range-start", "range-end", "in-range")
      const d = cell.dataset.date
      if (!this.anchor) return
      if (d === this.anchor) cell.classList.add("range-start")
      if (effectiveEnd) {
        if (d === effectiveEnd) cell.classList.add("range-end")
        else if (d > this.anchor && d < effectiveEnd) cell.classList.add("in-range")
      }
    })

    // While picking a check-out, any day after the anchor is a candidate
    // check-out target (incl. a period's closed end_date) — show it as such.
    this.gridTarget.classList.toggle("selecting", !this.readonlyValue && !!this.anchor && !this.end)
  }

  // Where a hover would extend the range to, if it forms a valid contiguous run.
  #previewEnd() {
    if (this.readonlyValue || !this.anchor || this.end || !this.hover || this.hover <= this.anchor) return null
    const nights = this.#nightsBetween(this.anchor, this.hover)
    if (!this.#allOpen(nights)) return null
    const max = this.days[this.anchor]?.max
    if (max && nights.length > max) return null
    return this.hover
  }

  #renderSummary() {
    if (!this.hasSummaryTarget) return
    if (this.anchor && this.end) {
      const nights = this.#nightsBetween(this.anchor, this.end).length
      this.summaryTarget.innerHTML =
        `<strong>${this.#human(this.anchor)} → ${this.#human(this.end)}</strong> · ${nights} night${nights === 1 ? "" : "s"}`
    } else if (this.anchor) {
      this.summaryTarget.textContent = `Check-in ${this.#human(this.anchor)} — now pick a check-out date.`
    } else if (!this.readonlyValue) {
      this.summaryTarget.textContent = "Select your check-in and check-out dates."
    } else {
      this.summaryTarget.textContent = ""
    }
  }

  #message(text) {
    if (this.hasSummaryTarget) this.summaryTarget.innerHTML = `<span class="wl-cal-msg">${text}</span>`
  }

  // --- interaction --------------------------------------------------------

  #onClick = (event) => {
    if (this.readonlyValue) return
    const cell = event.target.closest(".wl-cal-day[data-date]")
    if (!cell) return
    this.#selectDate(cell.dataset.date)
  }

  #onHover = (event) => {
    if (this.readonlyValue || !this.anchor || this.end) return
    const cell = event.target.closest(".wl-cal-day[data-date]")
    const d = cell ? cell.dataset.date : null
    if (d === this.hover) return
    this.hover = d
    this.#applyRangeClasses()
  }

  #onLeave = () => {
    if (!this.hover) return
    this.hover = null
    this.#applyRangeClasses()
  }

  #selectDate(date) {
    const info = this.days[date]
    if (!info) return // outside the loaded window
    const isOpen = info.s === "open"

    // No active selection, a completed one, or a click on/before the anchor:
    // (re)start. A stay can only BEGIN on an open night.
    if (!this.anchor || this.end || date <= this.anchor) {
      // Toggle a lone anchor back off.
      if (this.anchor === date && !this.end) {
        this.anchor = null
        this.hover = null
        this.#applyRangeClasses()
        this.#renderSummary()
        this.#commit()
        return
      }
      if (!isOpen) return // can't check in on a booked/closed/past night
      this.anchor = date
      this.end = null
      this.hover = null
      this.#applyRangeClasses()
      this.#renderSummary()
      return
    }

    // Complete the selection: anchor is set, date > anchor. `date` is the
    // CHECK-OUT day — the morning after the last night — so it need NOT be an
    // open night itself (it's legitimately the period's end_date, or the day a
    // booked stretch begins). Only every NIGHT in [anchor, date) must be open.
    const nights = this.#nightsBetween(this.anchor, date)
    if (!this.#allOpen(nights)) {
      this.#message("Some nights in that range aren't available.")
      if (isOpen) { // an open click can start a fresh selection
        this.anchor = date
        this.end = null
        this.#renderSummary()
      }
      this.#applyRangeClasses()
      return
    }
    const min = this.days[this.anchor].min || 1
    const max = this.days[this.anchor].max
    if (nights.length < min) {
      this.#applyRangeClasses()
      this.#message(`Minimum stay for these dates is ${min} ${min === 1 ? "night" : "nights"}.`)
      return
    }
    if (max && nights.length > max) {
      this.#applyRangeClasses()
      this.#message(`Maximum stay for these dates is ${max} nights.`)
      return
    }

    this.end = date
    this.hover = null
    this.#applyRangeClasses()
    this.#renderSummary()
    this.#commit()
  }

  // Push the selection into the hidden fields and notify the pricing form.
  #commit() {
    const ci = this.anchor && this.end ? this.anchor : ""
    const co = this.anchor && this.end ? this.end : ""
    if (this.hasCheckInFieldTarget) this.checkInFieldTarget.value = ci
    if (this.hasCheckOutFieldTarget) {
      this.checkOutFieldTarget.value = co
      // The reservation-form / stripe controllers listen on `change`.
      this.checkOutFieldTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
    if (!ci) this.#renderSummary()
  }

  // --- date helpers (all local-time to avoid UTC off-by-one) --------------

  #nightsBetween(startISO, endISO) {
    const out = []
    let d = this.#parse(startISO)
    const end = this.#parse(endISO)
    while (d < end) {
      out.push(this.#iso(d))
      d = this.#addDays(d, 1)
    }
    return out
  }

  #allOpen(isoList) {
    return isoList.every((iso) => this.days[iso] && this.days[iso].s === "open")
  }

  #parse(iso) {
    const [y, m, d] = iso.split("-").map(Number)
    return new Date(y, m - 1, d)
  }

  #iso(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }

  #human(iso) {
    const d = this.#parse(iso)
    return `${MONTH_NAMES[d.getMonth()].slice(0, 3)} ${d.getDate()}`
  }

  #addDays(date, n) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + n)
  }

  #addMonths(date, n) {
    return new Date(date.getFullYear(), date.getMonth() + n, 1)
  }

  #startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1)
  }

  #monthKey(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
  }
}
